import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Aperture, Sparkles, RotateCcw, ImagePlus, Lock, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getVoterId } from "@/lib/voter";
import { CinematicVideo } from "@/components/CinematicVideo";

const SHARE_URL = "cocoonmind.com";
const CHAMBRE_VIDEO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/7ezxejab_gemini_generated_video_ff966df3.mp4";

export const ChambreNoire = () => {
  const { t, i18n } = useTranslation();
  const inputRef = useRef(null);
  const [preview, setPreview] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | limit | error
  const [result, setResult] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [sharing, setSharing] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get("/chambre-noire/stats").then(({ data }) => setTotal(data.curiosities || 0)).catch(() => {});
  }, []);

  const pick = () => inputRef.current?.click();

  const reset = () => {
    setPreview((p) => { if (p) URL.revokeObjectURL(p); return ""; });
    setStatus("idle"); setResult(null); setErrMsg("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const compressImage = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const maxDim = 3000;
      let { width, height } = img;
      if (Math.max(width, height) > maxDim) {
        const r = maxDim / Math.max(width, height);
        width = Math.round(width * r); height = Math.round(height * r);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => resolve(blob || file), "image/jpeg", 0.9);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
    img.src = url;
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type && !file.type.startsWith("image/")) {
      setStatus("error"); setErrMsg(t("chambre.formatError")); return;
    }
    setStatus("loading"); setResult(null); setErrMsg("");
    let blob;
    try { blob = await compressImage(file); }
    catch { setStatus("error"); setErrMsg(t("chambre.formatError")); return; }
    if (blob.size > 20 * 1024 * 1024) {
      setStatus("error"); setErrMsg(t("chambre.sizeError")); return;
    }
    setPreview((p) => { if (p) URL.revokeObjectURL(p); return URL.createObjectURL(blob); });
    const fd = new FormData();
    fd.append("image", blob, "photo.jpg");
    fd.append("voter_id", getVoterId());
    fd.append("lang", i18n.language || "fr");
    try {
      const { data } = await api.post("/chambre-noire", fd);
      setResult(data);
      setRemaining(data.remaining);
      setTotal((n) => n + (data.curiosities?.length || 3));
      setStatus("done");
    } catch (err) {
      if (err.response?.status === 402) {
        setStatus("limit");
      } else {
        setStatus("error");
        setErrMsg(formatApiErrorDetail(err.response?.data?.detail));
      }
    }
  };

  const loadImg = (src) => new Promise((res) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = () => res(null);
    im.src = src;
  });

  const wrapText = (ctx, text, maxW) => {
    const words = (text || "").split(" ");
    const lines = [];
    let line = "";
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = w; }
      else line = test;
    }
    if (line) lines.push(line);
    return lines;
  };

  const buildCard = async () => {
    const W = 1080, H = 1350;
    const cv = document.createElement("canvas");
    cv.width = W; cv.height = H;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#0a0806"; ctx.fillRect(0, 0, W, H);
    const glow = ctx.createRadialGradient(W / 2, 210, 40, W / 2, 210, 760);
    glow.addColorStop(0, "rgba(240,155,45,0.30)"); glow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(240,170,90,0.35)"; ctx.lineWidth = 3;
    if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(28, 28, W - 56, H - 56, 40); ctx.stroke(); }

    const brand = await loadImg("/cocoon-logo-glow.png");
    let headY = 108, startY = 150;
    if (brand && brand.height) {
      const bh = 120, bw = bh * (brand.width / brand.height);
      ctx.save();
      ctx.shadowColor = "rgba(240,155,45,0.45)"; ctx.shadowBlur = 34;
      ctx.drawImage(brand, (W - bw) / 2, 46, bw, bh);
      ctx.restore();
      headY = 214; startY = 256;
    }

    ctx.textAlign = "center";
    try { ctx.letterSpacing = "8px"; } catch { /* noop */ }
    ctx.fillStyle = "rgba(240,175,95,0.92)";
    ctx.font = "600 27px Georgia, serif";
    ctx.fillText("LA CHAMBRE NOIRE · COCOON", W / 2, headY);
    try { ctx.letterSpacing = "0px"; } catch { /* noop */ }

    let y = startY;
    if (preview) {
      const im = await loadImg(preview);
      if (im) {
        const size = 330, x = (W - size) / 2;
        ctx.save();
        if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, size, size, 28); ctx.clip(); }
        const ratio = Math.max(size / im.width, size / im.height);
        const dw = im.width * ratio, dh = im.height * ratio;
        ctx.drawImage(im, x + (size - dw) / 2, y + (size - dh) / 2, dw, dh);
        ctx.restore();
        ctx.strokeStyle = "rgba(240,170,90,0.5)"; ctx.lineWidth = 2;
        if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, size, size, 28); ctx.stroke(); }
        y += size + 62;
      } else { y += 40; }
    } else { y += 40; }

    if (result?.caption) {
      ctx.fillStyle = "#f4ede2";
      ctx.font = "italic 46px Georgia, serif";
      const lines = wrapText(ctx, `« ${result.caption} »`, W - 200).slice(0, 3);
      for (const l of lines) { ctx.fillText(l, W / 2, y); y += 62; }
      y += 26;
    }

    ctx.textAlign = "left";
    const mx = 120;
    for (let i = 0; i < (result?.curiosities || []).length; i++) {
      const c = result.curiosities[i];
      ctx.fillStyle = "rgba(240,155,45,0.18)";
      if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(mx, y - 34, 46, 46, 23); ctx.fill(); }
      ctx.fillStyle = "rgba(245,180,95,1)"; ctx.font = "600 26px Georgia, serif"; ctx.textAlign = "center";
      ctx.fillText(String(i + 1), mx + 23, y);
      ctx.textAlign = "left";
      ctx.fillStyle = "#f0c88a"; ctx.font = "600 34px Georgia, serif";
      const tl = wrapText(ctx, c.title || c.text, W - mx - 190).slice(0, 2);
      let ty = y;
      for (const l of tl) { ctx.fillText(l, mx + 72, ty); ty += 42; }
      y = ty + 42;
    }

    ctx.textAlign = "center";
    if (total > 0) {
      ctx.fillStyle = "rgba(245,180,95,0.85)"; ctx.font = "600 28px Georgia, serif";
      ctx.fillText(t("chambre.counter", { count: total.toLocaleString() }), W / 2, H - 148);
    }
    ctx.fillStyle = "rgba(255,255,255,0.55)"; ctx.font = "500 30px Georgia, serif";
    ctx.fillText(t("chambre.shareFooter"), W / 2, H - 96);
    ctx.fillStyle = "rgba(245,180,95,1)"; ctx.font = "700 40px Georgia, serif";
    ctx.fillText(SHARE_URL, W / 2, H - 48);

    return new Promise((res) => cv.toBlob((b) => res(b), "image/png"));
  };

  const shareCard = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const blob = await buildCard();
      const file = new File([blob], "chambre-noire-cocoon.png", { type: "image/png" });
      const shareText = t("chambre.shareText");
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], title: "COCOON — La Chambre noire", text: shareText }); setSharing(false); return; }
        catch { /* annulé ou non supporté → fallback */ }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "chambre-noire-cocoon.png"; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("chambre.shareDownloaded"));
    } catch {
      toast.error(t("chambre.error"));
    } finally { setSharing(false); }
  };

  return (
    <section className="relative px-6 my-20" data-testid="chambre-noire">
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-[2rem] overflow-hidden border border-primary/20 frame-pulse chambre-shell">
          <div className="chambre-aura" aria-hidden="true" />

          <div className="relative z-10 px-6 sm:px-12 py-14 text-center">
            <div className="max-w-2xl mx-auto mb-10">
              <CinematicVideo
                silver
                src={CHAMBRE_VIDEO}
                testId="chambre-cinematic-video"
                poster="/chambre-noire-poster.jpg"
                caption={t("chambre.subtitle")}
                fade
                noSound
              />
            </div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-primary text-[11px] uppercase tracking-[0.28em] mb-6" data-testid="chambre-badge">
              <Sparkles size={13} /> {t("chambre.badge")}
            </span>
            <h2 className="font-display text-gradient-orange text-4xl sm:text-6xl leading-[1.12] pb-[0.08em] mb-4" data-testid="chambre-title">
              {t("chambre.title")}
            </h2>
            <p className="text-foreground/65 text-base sm:text-lg max-w-xl mx-auto leading-relaxed mb-5">
              {t("chambre.subtitle")}
            </p>
            {total > 0 && (
              <p className="inline-flex items-center gap-2 text-primary/70 text-sm mb-9" data-testid="chambre-counter">
                <Sparkles size={14} /> {t("chambre.counter", { count: total.toLocaleString() })}
              </p>
            )}

            <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="chambre-file-input" />

            <AnimatePresence mode="wait">
              {status === "idle" && (
                <motion.div key="idle" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <button onClick={pick} className="btn-frost-orange inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-base" data-testid="chambre-upload-btn">
                    <ImagePlus size={20} /> {t("chambre.cta")}
                  </button>
                  <p className="text-foreground/40 text-sm mt-5 italic">{t("chambre.hint")}</p>
                </motion.div>
              )}

              {status === "loading" && (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center">
                  <div className="chambre-develop relative w-52 h-52 rounded-2xl overflow-hidden mb-6" data-testid="chambre-developing">
                    {preview && <img src={preview} alt="" className="w-full h-full object-cover" />}
                    <div className="chambre-develop-veil" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Aperture size={34} className="text-primary animate-spin" style={{ animationDuration: "3s" }} />
                    </div>
                  </div>
                  <p className="text-primary/80 text-sm uppercase tracking-[0.25em] animate-pulse">{t("chambre.developing")}</p>
                </motion.div>
              )}

              {status === "done" && result && (
                <motion.div key="done" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="flex flex-col sm:flex-row items-center gap-5 mb-8">
                    {preview && (
                      <motion.img initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
                        src={preview} alt="" className="w-28 h-28 rounded-2xl object-cover border border-primary/30 flex-shrink-0" data-testid="chambre-result-image" />
                    )}
                    {result.caption && (
                      <p className="font-display italic text-xl sm:text-2xl text-foreground/85 text-left leading-snug" data-testid="chambre-caption">
                        « {result.caption} »
                      </p>
                    )}
                  </div>
                  <div className="grid gap-4 text-left">
                    {result.curiosities.map((c, i) => (
                      <motion.div key={i} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 + i * 0.3 }}
                        className="glass rounded-2xl p-5 border border-white/8" data-testid={`chambre-curiosity-${i}`}>
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center font-display text-sm">{i + 1}</span>
                          <div>
                            {c.title && <h3 className="font-display text-lg text-primary/90 mb-1">{c.title}</h3>}
                            <p className="text-foreground/70 leading-relaxed text-[15px]">{c.text}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <div className="mt-8 flex flex-col items-center gap-3">
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <button onClick={shareCard} disabled={sharing} className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm disabled:opacity-60" data-testid="chambre-share-btn">
                        {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />} {t("chambre.share")}
                      </button>
                      <button onClick={reset} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm" data-testid="chambre-again-btn">
                        <RotateCcw size={16} /> {t("chambre.again")}
                      </button>
                    </div>
                    {remaining != null && !result.unlimited && (
                      <p className="text-foreground/40 text-xs" data-testid="chambre-remaining">{t("chambre.remaining", { count: remaining })}</p>
                    )}
                  </div>
                </motion.div>
              )}

              {status === "limit" && (
                <motion.div key="limit" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex flex-col items-center" data-testid="chambre-limit">
                  <span className="w-14 h-14 rounded-full bg-primary/15 text-primary flex items-center justify-center mb-4"><Lock size={24} /></span>
                  <p className="text-foreground/75 max-w-md leading-relaxed mb-6">{t("chambre.limit")}</p>
                  <Link to="/entrer" className="btn-frost-orange inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold" data-testid="chambre-limit-cta">
                    <Sparkles size={17} /> {t("chambre.limitCta")}
                  </Link>
                </motion.div>
              )}

              {status === "error" && (
                <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center">
                  <p className="text-foreground/70 mb-5">{errMsg || t("chambre.error")}</p>
                  <button onClick={reset} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm" data-testid="chambre-retry-btn">
                    <RotateCcw size={16} /> {t("chambre.again")}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
